import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, Search, Layers, BookOpen, LayoutGrid, Table2, ArrowUpDown, ChevronUp, ChevronDown, Users } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import usePersistentState from "@/hooks/usePersistentState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrackLibrary() {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistentState("tracks:search", "");
  const [filterStatus, setFilterStatus] = usePersistentState("tracks:filterStatus", "all");
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", status: "draft" });
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("track_view") || "card");
  const updateView = (v) => { setViewMode(v); localStorage.setItem("track_view", v); };
  const [sortField, setSortField] = useState("title");
  const [sortDir, setSortDir] = useState("asc");

  const fetchTracks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tracks`, { withCredentials: true });
      setTracks(data);
    } catch {
      toast.error("Failed to load tracks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTracks(); }, [fetchTracks]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tracks`, form, { withCredentials: true });
      toast.success("Track created");
      setShowCreate(false);
      setForm({ title: "", description: "", status: "draft" });
      fetchTracks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create track");
    } finally { setSaving(false); }
  };

  const filtered = tracks.filter((t) => {
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || t.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    if (sortField === "title") { va = a.title || ""; vb = b.title || ""; }
    else if (sortField === "status") { va = a.status || ""; vb = b.status || ""; }
    else if (sortField === "items") { va = a.item_count || 0; vb = b.item_count || 0; }
    else if (sortField === "cohorts") { va = (a.cohort_assignments || []).length; vb = (b.cohort_assignments || []).length; }
    else if (sortField === "progress") { va = a.learner_completed || 0; vb = b.learner_completed || 0; }
    else { va = a.title || ""; vb = b.title || ""; }
    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === "asc" ? va - vb : vb - va;
  });

  const SortIcon = ({ field }) => sortField === field
    ? (sortDir === "asc" ? <ChevronUp className="h-3 w-3 inline ml-0.5" /> : <ChevronDown className="h-3 w-3 inline ml-0.5" />)
    : <ArrowUpDown className="h-3 w-3 inline ml-0.5 opacity-30" />;

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#6E7787]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="track-library-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="track-library-title">Track Library</h1>
          <p className="text-sm text-[#6E7787] font-body mt-1">Build and Manage Instructional Tracks</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-track-button"
          className="font-body text-white h-10 px-4 rounded-xl"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
          <Plus className="h-4 w-4 mr-2" /> Create Track
        </Button>
      </div>

      {/* Filters + view toggle */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-3 items-center">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
            <Input placeholder="Search tracks..." value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="track-search-input" className="pl-10 bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-10" />
          </div>
          <div className="flex gap-2">
            {["all", "published", "draft"].map((s) => (
              <button key={s} onClick={() => setFilterStatus(s)} data-testid={`filter-${s}`}
                className={`px-3 py-2 text-sm font-body rounded-lg transition-colors ${filterStatus === s ? "text-white bg-white/10" : "text-[#6E7787] hover:text-white hover:bg-white/5"}`}>
                {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-16 bg-[#11141A] border border-white/10 rounded-xl" data-testid="tracks-empty">
          <Layers className="h-10 w-10 text-[#6E7787] mx-auto mb-3" />
          <p className="text-[#6E7787] font-body">No tracks yet. Create your first track to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-[#11141A] border border-white/10" data-testid="tracks-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort("title")}>Title <SortIcon field="title" /></th>
                <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort("status")}>Status <SortIcon field="status" /></th>
                <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort("items")}>Contents <SortIcon field="items" /></th>
                <th className="text-right px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort("progress")}>Learner Progress <SortIcon field="progress" /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr key={t.id} onClick={() => navigate(`/admin/tracks/${t.id}`)}
                  className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                  data-testid={`track-row-${t.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-body font-medium text-white">{t.title}</span>
                      </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={t.status === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" : "bg-amber-500/15 text-amber-400 border-amber-500/20"}>
                      {t.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm font-body text-[#A1A9B8]">
                    {t.lexp_count || 0} LEXP{(t.lexp_count || 0) !== 1 ? "s" : ""}{(t.assessment_count || 0) > 0 ? `, ${t.assessment_count} Assessment${t.assessment_count !== 1 ? "s" : ""}` : ""}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {t.learner_enrolled > 0 ? (
                      <span className="text-sm font-body text-[#A1A9B8]">{t.learner_completed}/{t.learner_enrolled}</span>
                    ) : (
                      <span className="text-xs text-[#6E7787] font-body">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-md" data-testid="create-track-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">Create Track</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body text-sm">Create a new track to group learning experiences.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-[#A1A9B8] font-body text-sm">Title <span className="text-red-400">*</span></Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Cardiac Assessment Track"
                data-testid="track-title-input" className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-10" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#A1A9B8] font-body text-sm">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description..."
                rows={3} data-testid="track-description-input" className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] resize-none" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#A1A9B8] font-body text-sm">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-[#0A0C10] border-white/10 text-white h-10" data-testid="track-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11141A] border-white/10">
                  <SelectItem value="draft" className="text-white font-body">Draft</SelectItem>
                  <SelectItem value="published" className="text-white font-body">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="font-body text-[#A1A9B8] hover:text-white" data-testid="track-cancel-button">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="track-save-button"
              className="font-body text-white rounded-xl px-6" style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
